// per specs/060-agreed-alerts/plan.md C4 (BFF /api/v1/admin/watched-groups) and C2 (member shape)
// The watched medicine group the System Administrator keeps once and every alert refers to
// (caiet TF-127, TF-128). The shapes are declared here until the shared types of the group
// (libs/shared/types/src/lib/watched-group.ts) replace them at integration.
import { apiClient } from '../../app/api-client';
import type { HistoryEntry } from '../../features/history/api';

export const WATCHED_GROUPS_KEY = 'watched-groups';

export const THRESHOLD_KINDS = ['QUANTITY', 'PERCENT'] as const;
export type ThresholdKind = (typeof THRESHOLD_KINDS)[number];

export type MemberKind = 'MEDICINE' | 'DCI_DOSE_FORM';

export interface WatchedGroupMember {
  id: string;
  kind: MemberKind;
  medicineId: string | null;
  /** Commercial name + DCI + dose + form of the medicine, for a MEDICINE member. */
  medicineLabel: string;
  dci: string;
  dose: string;
  form: string;
}

export interface WatchedGroup {
  id: string;
  name: string;
  description: string;
  thresholdKind: ThresholdKind | null;
  thresholdValue: number | null;
  active: boolean;
  memberCount: number;
  members: WatchedGroupMember[];
  updatedAt: string | null;
}

export interface DciOption {
  dci: string;
  dose: string;
  form: string;
}

export type AddMemberBody =
  | { kind: 'MEDICINE'; medicineId: string }
  | { kind: 'DCI_DOSE_FORM'; dci: string; dose: string; form: string };

export interface GroupPayload {
  name: string;
  description: string | null;
  thresholdKind: ThresholdKind | null;
  thresholdValue: number | null;
  active: boolean;
}

type Raw = Record<string, unknown>;

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// A response is either the bare array or an object holding it under one of the keys the BFF
// lists use; the contract fixes the routes and the fields, not the envelope.
function listOf(data: unknown, keys: readonly string[]): Raw[] {
  if (Array.isArray(data)) return data as Raw[];
  if (data !== null && typeof data === 'object') {
    for (const key of keys) {
      const found = (data as Raw)[key];
      if (Array.isArray(found)) return found as Raw[];
    }
  }
  return [];
}

function toMember(raw: Raw): WatchedGroupMember {
  return {
    id: text(raw['id']),
    kind: raw['kind'] === 'DCI_DOSE_FORM' ? 'DCI_DOSE_FORM' : 'MEDICINE',
    medicineId: typeof raw['medicineId'] === 'string' ? raw['medicineId'] : null,
    medicineLabel: text(raw['medicineLabel']),
    dci: text(raw['dci']),
    dose: text(raw['dose']),
    form: text(raw['form']),
  };
}

export function toGroup(raw: Raw): WatchedGroup {
  const members = listOf(raw['members'], []).map(toMember);
  const kind = raw['thresholdKind'];
  const thresholdKind = kind === 'QUANTITY' || kind === 'PERCENT' ? kind : null;
  const count = numberOrNull(raw['memberCount']);
  return {
    id: text(raw['id']),
    name: text(raw['name']),
    description: text(raw['description']),
    thresholdKind,
    thresholdValue: thresholdKind ? numberOrNull(raw['thresholdValue']) : null,
    active: raw['active'] !== false,
    memberCount: count ?? members.length,
    members,
    updatedAt: typeof raw['updatedAtIso'] === 'string' ? raw['updatedAtIso'] : null,
  };
}

const BASE = '/admin/watched-groups';

export async function listWatchedGroups(): Promise<WatchedGroup[]> {
  const { data } = await apiClient.get<unknown>(BASE);
  return listOf(data, ['groups', 'items']).map(toGroup);
}

export async function getWatchedGroup(id: string): Promise<WatchedGroup> {
  const { data } = await apiClient.get<Raw>(`${BASE}/${encodeURIComponent(id)}`);
  return toGroup(data);
}

export async function createWatchedGroup(payload: GroupPayload): Promise<WatchedGroup> {
  const { data } = await apiClient.post<Raw>(BASE, payload);
  return toGroup(data);
}

export async function updateWatchedGroup(id: string, payload: GroupPayload): Promise<WatchedGroup> {
  const { data } = await apiClient.patch<Raw>(`${BASE}/${encodeURIComponent(id)}`, payload);
  return toGroup(data);
}

export async function deleteWatchedGroup(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
}

export async function addWatchedGroupMember(
  groupId: string,
  body: AddMemberBody,
): Promise<WatchedGroup> {
  const { data } = await apiClient.post<Raw>(
    `${BASE}/${encodeURIComponent(groupId)}/members`,
    body,
  );
  return toGroup(data);
}

export async function removeWatchedGroupMember(groupId: string, memberId: string): Promise<void> {
  await apiClient.delete(
    `${BASE}/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`,
  );
}

export async function fetchDciOptions(q: string): Promise<DciOption[]> {
  const { data } = await apiClient.get<unknown>(`${BASE}/dci-options`, { params: { q } });
  return listOf(data, ['options', 'items']).map((raw) => ({
    dci: text(raw['dci']),
    dose: text(raw['dose']),
    form: text(raw['form']),
  }));
}

export async function fetchWatchedGroupHistory(id: string): Promise<HistoryEntry[]> {
  const { data } = await apiClient.get<unknown>(`${BASE}/${encodeURIComponent(id)}/history`);
  return listOf(data, ['entries', 'items']).map((raw) => {
    const entry = raw as unknown as HistoryEntry;
    return { ...entry, changes: Array.isArray(entry.changes) ? entry.changes : [] };
  });
}
